// ============================================================================
// ENHANCED FORMATTERS UTILITY - Foundations + Insights Integration
// Professional financial formatting and risk level categorization
// ============================================================================

/**
 * Format currency values with appropriate suffixes (K, M, B) - from Insights
 * @param {number} amount - The amount to format
 * @param {object} options - Formatting options
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, options = {}) => {
  const { 
    showCents = false, 
    showSign = false,
    compact = true
  } = options;

  if (!amount && amount !== 0) return '$0';
  
  // Handle negative values
  const isNegative = amount < 0;
  const absAmount = Math.abs(amount);
  const sign = isNegative ? '-' : (showSign ? '+' : '');
  
  if (compact) {
    if (absAmount >= 1000000000) {
      return `${sign}$${(absAmount / 1000000000).toFixed(1)}B`;
    }
    if (absAmount >= 1000000) {
      return `${sign}$${(absAmount / 1000000).toFixed(1)}M`;
    }
    if (absAmount >= 1000) {
      return `${sign}$${(absAmount / 1000).toFixed(0)}K`;
    }
  }
  
  if (showCents) {
    return `${sign}$${absAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  
  return `${sign}$${Math.round(absAmount).toLocaleString('en-US')}`;
};

/**
 * Format risk level based on score with color coding - from Insights
 * @param {number} score - Risk score (0-100)
 * @returns {object} Object with level, color, icon, and description
 */
export const formatRiskLevel = (score) => {
  if (!score && score !== 0) {
    return { 
      level: 'Unknown', 
      color: '#6b7280', 
      backgroundColor: '#f3f4f6',
      icon: '❓',
      description: 'Assessment incomplete'
    };
  }
  
  if (score < 30) {
    return { 
      level: 'Low', 
      color: '#10b981',
      backgroundColor: '#d1fae5', 
      icon: '🟢',
      description: 'Minimal risk exposure'
    };
  }
  if (score < 50) {
    return { 
      level: 'Medium', 
      color: '#f59e0b',
      backgroundColor: '#fef3c7', 
      icon: '🟡',
      description: 'Moderate risk requires attention'
    };
  }
  if (score < 70) {
    return { 
      level: 'High', 
      color: '#ef4444',
      backgroundColor: '#fee2e2', 
      icon: '🟠',
      description: 'Significant risk exposure'
    };
  }
  return { 
    level: 'Critical', 
    color: '#dc2626',
    backgroundColor: '#fecaca', 
    icon: '🔴',
    description: 'Urgent action required'
  };
};

/**
 * Format recovery capability based on days - from Insights enhanced
 * @param {number} days - Number of days to recover
 * @param {number} riskScore - Risk score for context
 * @returns {object} Object with level, color, and description
 */
export const formatRecoveryCapability = (days, riskScore = 0) => {
  if (!days && days !== 0) {
    return { 
      level: 'Unknown', 
      color: '#6b7280',
      backgroundColor: '#f3f4f6',
      icon: '❓',
      description: 'Recovery time not assessed'
    };
  }
  
  if (days <= 1) {
    return { 
      level: 'Excellent', 
      color: '#10b981',
      backgroundColor: '#d1fae5',
      icon: '⚡',
      description: 'Rapid recovery capability'
    };
  }
  if (days <= 3) {
    return { 
      level: 'Good', 
      color: '#3b82f6',
      backgroundColor: '#dbeafe',
      icon: '✅',
      description: 'Strong recovery processes'
    };
  }
  if (days <= 7) {
    return { 
      level: 'Fair', 
      color: '#f59e0b',
      backgroundColor: '#fef3c7',
      icon: '⚠️',
      description: 'Moderate recovery capability'
    };
  }
  if (days <= 14) {
    return { 
      level: 'Poor', 
      color: '#ef4444',
      backgroundColor: '#fee2e2',
      icon: '🚨',
      description: 'Slow recovery processes'
    };
  }
  return { 
    level: 'Critical', 
    color: '#dc2626',
    backgroundColor: '#fecaca',
    icon: '💥',
    description: 'Recovery capabilities at risk'
  };
};

/**
 * Format percentage with proper styling
 * @param {number} value - Percentage value (0-100)
 * @param {object} options - Formatting options
 * @returns {string} Formatted percentage
 */
export const formatPercentage = (value, options = {}) => {
  const { decimalPlaces = 1, showSign = false } = options;
  
  if (!value && value !== 0) return '0%';
  
  const sign = showSign && value > 0 ? '+' : '';
  return `${sign}${value.toFixed(decimalPlaces)}%`;
};

/**
 * Format industry name for display
 * @param {string} industry - Industry name
 * @returns {string} Formatted industry name
 */
export const formatIndustry = (industry) => {
  if (!industry) return 'Unknown';
  
  // Handle common variations
  const industryMap = {
    'tech': 'Technology',
    'healthcare': 'Healthcare',
    'financial': 'Financial Services',
    'manufacturing': 'Manufacturing',
    'retail': 'Retail',
    'energy': 'Energy',
    'education': 'Education',
    'government': 'Government',
    'hospitality and tourism': 'Hospitality & Tourism',
    'media': 'Media & Entertainment',
    'legal': 'Legal Services',
    'transportation': 'Transportation'
  };
  
  const normalizedIndustry = industry.toLowerCase();
  return industryMap[normalizedIndustry] || industry;
};

/**
 * Format company size based on employee count or revenue
 * @param {number} employeeCount - Number of employees
 * @param {number} revenue - Annual revenue
 * @returns {object} Size category with description
 */
export const formatCompanySize = (employeeCount = 0, revenue = 0) => {
  // Primary categorization by employee count
  if (employeeCount > 0) {
    if (employeeCount < 50) {
      return { 
        size: 'Small', 
        category: 'Startup/Small Business',
        color: '#3b82f6',
        icon: '🏢'
      };
    }
    if (employeeCount < 250) {
      return { 
        size: 'Medium', 
        category: 'Mid-Market',
        color: '#f59e0b',
        icon: '🏬'
      };
    }
    if (employeeCount < 1000) {
      return { 
        size: 'Large', 
        category: 'Large Enterprise',
        color: '#ef4444',
        icon: '🏭'
      };
    }
    return { 
      size: 'Enterprise', 
      category: 'Fortune 500',
      color: '#8b5cf6',
      icon: '🏛️'
    };
  }
  
  // Fallback to revenue-based categorization
  if (revenue > 0) {
    if (revenue < 10000000) { // < $10M
      return { 
        size: 'Small', 
        category: 'Small Business',
        color: '#3b82f6',
        icon: '🏢'
      };
    }
    if (revenue < 100000000) { // < $100M
      return { 
        size: 'Medium', 
        category: 'Mid-Market',
        color: '#f59e0b',
        icon: '🏬'
      };
    }
    return { 
      size: 'Large', 
      category: 'Enterprise',
      color: '#ef4444',
      icon: '🏭'
    };
  }
  
  return { 
    size: 'Unknown', 
    category: 'Size Unknown',
    color: '#6b7280',
    icon: '❓'
  };
};

/**
 * Format time since last assessment
 * @param {string|Date} lastAssessment - Last assessment date
 * @returns {object} Time format with urgency level
 */
export const formatLastAssessment = (lastAssessment) => {
  if (!lastAssessment) {
    return {
      text: 'Never',
      urgency: 'critical',
      color: '#dc2626',
      icon: '🚨'
    };
  }
  
  const now = new Date();
  const assessmentDate = new Date(lastAssessment);
  const daysDiff = Math.floor((now - assessmentDate) / (1000 * 60 * 60 * 24));
  
  if (daysDiff === 0) {
    return {
      text: 'Today',
      urgency: 'current',
      color: '#10b981',
      icon: '✅'
    };
  }
  
  if (daysDiff === 1) {
    return {
      text: '1 day ago',
      urgency: 'current',
      color: '#10b981',
      icon: '✅'
    };
  }
  
  if (daysDiff < 7) {
    return {
      text: `${daysDiff} days ago`,
      urgency: 'recent',
      color: '#3b82f6',
      icon: '📅'
    };
  }
  
  if (daysDiff < 30) {
    return {
      text: `${daysDiff} days ago`,
      urgency: 'aging',
      color: '#f59e0b',
      icon: '⚠️'
    };
  }
  
  if (daysDiff < 90) {
    return {
      text: `${Math.floor(daysDiff / 7)} weeks ago`,
      urgency: 'stale',
      color: '#ef4444',
      icon: '📊'
    };
  }
  
  return {
    text: `${Math.floor(daysDiff / 30)} months ago`,
    urgency: 'critical',
    color: '#dc2626',
    icon: '🚨'
  };
};

/**
 * Format revenue at risk as percentage of total revenue
 * @param {number} revenueAtRisk - Revenue at risk amount
 * @param {number} totalRevenue - Total annual revenue
 * @returns {object} Formatted risk percentage with severity
 */
export const formatRevenueRiskPercentage = (revenueAtRisk, totalRevenue) => {
  if (!totalRevenue || totalRevenue === 0) {
    return {
      percentage: '0%',
      severity: 'unknown',
      color: '#6b7280',
      description: 'Unable to calculate risk percentage'
    };
  }
  
  const percentage = (revenueAtRisk / totalRevenue) * 100;
  
  if (percentage < 1) {
    return {
      percentage: formatPercentage(percentage, { decimalPlaces: 2 }),
      severity: 'low',
      color: '#10b981',
      description: 'Minimal revenue exposure'
    };
  }
  
  if (percentage < 5) {
    return {
      percentage: formatPercentage(percentage),
      severity: 'moderate',
      color: '#f59e0b',
      description: 'Manageable revenue risk'
    };
  }
  
  if (percentage < 15) {
    return {
      percentage: formatPercentage(percentage),
      severity: 'high',
      color: '#ef4444',
      description: 'Significant revenue exposure'
    };
  }
  
  return {
    percentage: formatPercentage(percentage),
    severity: 'critical',
    color: '#dc2626',
    description: 'Critical revenue at risk'
  };
};

/**
 * Create a formatted summary object for portfolio metrics
 * @param {object} portfolioBI - Business intelligence data
 * @returns {object} Formatted summary with all key metrics
 */
export const formatPortfolioSummary = (portfolioBI) => {
  if (!portfolioBI || !portfolioBI.aggregatedMetrics) {
    return {
      totalRevenueAtRisk: formatCurrency(0),
      avgRecoveryDays: '0 days',
      riskDistribution: 'No data',
      totalCompanies: 0,
      status: 'No data available'
    };
  }
  
  const { aggregatedMetrics, riskDistribution, companiesAnalyzed } = portfolioBI;
  
  const recoveryText = aggregatedMetrics.avgRecoveryDays === 1 
    ? '1 day' 
    : `${aggregatedMetrics.avgRecoveryDays} days`;
  
  const highestRiskCategory = Object.keys(riskDistribution).reduce((a, b) => 
    riskDistribution[a] > riskDistribution[b] ? a : b
  );
  
  return {
    totalRevenueAtRisk: formatCurrency(aggregatedMetrics.totalRevenueAtRisk),
    avgRecoveryDays: recoveryText,
    riskDistribution: `${riskDistribution.critical + riskDistribution.high}/${companiesAnalyzed} high/critical risk`,
    totalCompanies: companiesAnalyzed,
    primaryRiskLevel: highestRiskCategory,
    revenueRiskPercentage: formatPercentage(aggregatedMetrics.revenueAtRiskPercentage),
    status: companiesAnalyzed > 0 ? 'Active portfolio' : 'Empty portfolio'
  };
};

// Export all formatters as named exports
export default {
  formatCurrency,
  formatRiskLevel,
  formatRecoveryCapability,
  formatPercentage,
  formatIndustry,
  formatCompanySize,
  formatLastAssessment,
  formatRevenueRiskPercentage,
  formatPortfolioSummary
};